/* eslint-disable camelcase, no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";

import User from "../../../../User";
import Filters from "../../../../filters";
import { mapNachaPassthroughSubmissionFields } from "../nachaPassthrough/helpers";

import type {
  DomesticWireDualApprovalState,
  NachaPassthroughDualApprovalState,
  DualApprovalSlice,
  StatusType,
  AnyDualApprovalState,
} from "./types";
import type {
  DualApprovalPayload,
  DualApprovalsPayload,
} from "../../../../types/models/v1";

type PaymentWithSameDay = { is_same_day?: boolean };

const initialState: DualApprovalSlice = {
  byId: {},
  pageMap: {},
  total: 0,
  templatePageMap: {},
  templateTotal: 0,
  historyPageMap: {},
  historyTotal: 0,
};

export const isWireDualApproval = (
  da: API.AnyDualApproval,
): da is API.DomesticWireDualApproval => {
  return "wire" in da && !!da.wire;
};

export const isACHDualApproval = (
  da: API.AnyDualApproval,
): da is API.ACHDualApproval => {
  return "ach_payment" in da && !!da.ach_payment;
};

export const isFedNowDualApproval = (
  da: API.AnyDualApproval,
): da is API.FedNowDualApproval => {
  return "fednow_transaction" in da && !!da.fednow_transaction;
};

export const isACHReversalDualApproval = (
  da: API.AnyDualApproval,
): da is API.ACHReversalDualApproval => {
  return "unverified_ach_reversal" in da && !!da.unverified_ach_reversal;
};

export const isScheduledACHDualApproval = (
  da: API.AnyDualApproval,
): da is API.ScheduledACHDualApproval => {
  return "scheduled_ach_payment" in da && !!da.scheduled_ach_payment;
};

export const isStoreWireDualApproval = (
  da: AnyDualApprovalState,
): da is DomesticWireDualApprovalState => {
  return "wire" in da && !!da.wire;
};

/** Narrows any stored dual approval (or a union of them) to a pass-through file. */
export const isStoreNachaPassthroughDualApproval = <T extends object>(
  da: T,
): da is T & NachaPassthroughDualApprovalState =>
  "nachaPassthroughSubmission" in da && !!da.nachaPassthroughSubmission;

export const isBulkDualApproval = (
  da: API.AnyDualApproval,
): da is API.BulkDualApproval => {
  return "bulk_payment" in da && !!da.bulk_payment;
};

export const isBulkTemplateDualApproval = (
  da: API.AnyDualApproval,
): da is API.BulkPaymentTemplateVersionDualApproval => {
  return (
    "bulk_payment_template_version" in da && !!da.bulk_payment_template_version
  );
};

export const isInternationalWireDualApproval = (
  da: API.AnyDualApproval,
): da is API.InternationalWireDualApproval => {
  return "international_wire" in da && !!da.international_wire;
};

export const isWireImportBatchDualApproval = (
  da: API.AnyDualApproval,
): da is API.WireImportBatchDualApproval => {
  return "wire_import_batch" in da && !!da.wire_import_batch;
};

export const isNachaPassthroughDualApproval = (
  da: API.AnyDualApproval,
): da is API.NachaPassthroughDualApproval => {
  return (
    "nacha_passthrough_submission" in da && !!da.nacha_passthrough_submission
  );
};

const isBulkReversalDualApproval = (
  da: API.AnyDualApproval,
): da is API.BulkReversalDualApproval => {
  return (
    "bulk_reversal" in da && !!(da as { bulk_reversal?: unknown }).bulk_reversal
  );
};

const isSingular = <
  T extends { approval_request: API.AnyDualApproval },
  TS extends { approval_requests: API.AnyDualApproval[] },
>(
  payload: T | TS,
): payload is T => "approval_request" in payload;

const prepare = (approvalData: DualApprovalPayload | DualApprovalsPayload) => {
  if (isSingular(approvalData)) {
    return {
      payload: {
        dualApproval: approvalData.approval_request,
      },
    };
  }
  return {
    payload: {
      dualApprovals: approvalData.approval_requests,
    },
  };
};

export const STATUS_TYPE_FROM_STATE: Readonly<Record<string, StatusType>> =
  Object.freeze({
    analyzing: "approved",
    pending: "approved",
    review: "approved",
    processing: "approved",
    processed: "approved",
    approved: "approved",
    rejected: "rejected",
    canceled: "rejected",
    canceled_by_user: "rejected",
    awaiting_approval: "pending",
    pending_approval: "pending",
    rejected_approval: "rejected",
    schedule_approved: "approved",
    schedule_awaiting_approval: "pending",
    schedule_rejected: "rejected",
    approval_expired: "expired",
    expired: "expired",
    in_progress: "approved",
    scheduled: "approved",
    bulk_payment_canceled: "rejected",
    bulk_reversal_canceled: "rejected",
    wire_import_canceled: "rejected",
    partially_completed: "approved",
    completed: "approved",
    failed: "approved",
  });

const getStatus = (da: API.AnyDualApproval): StatusType => {
  if (isBulkTemplateDualApproval(da)) {
    const state = da.bulk_payment_template_version.dual_approval_state;
    if (state && STATUS_TYPE_FROM_STATE[state]) {
      return STATUS_TYPE_FROM_STATE[state];
    }
    return "pending";
  }

  if (isNachaPassthroughDualApproval(da)) {
    const { state } = da.nacha_passthrough_submission;
    if (state && STATUS_TYPE_FROM_STATE[state]) {
      return STATUS_TYPE_FROM_STATE[state];
    }
    return "pending";
  }

  const relatedObject =
    (da as API.DomesticWireDualApproval).wire ||
    (da as API.ACHDualApproval).ach_payment ||
    (da as API.FedNowDualApproval).fednow_transaction ||
    (da as API.ACHReversalDualApproval).unverified_ach_reversal ||
    (da as API.FedNowDualApproval).fednow_transaction ||
    (da as API.BulkReversalDualApproval).bulk_reversal ||
    (da as API.ScheduledACHDualApproval).scheduled_ach_payment ||
    (da as API.BulkDualApproval).bulk_payment ||
    (da as API.InternationalWireDualApproval).international_wire ||
    (da as API.WireImportBatchDualApproval).wire_import_batch;
  if (!relatedObject) {
    return "unknown";
  }
  const state =
    relatedObject.state ||
    (relatedObject as unknown as API.ScheduledACHDualApproval)
      .dual_approval_state;
  if (STATUS_TYPE_FROM_STATE[state]) {
    return STATUS_TYPE_FROM_STATE[state];
  }
  // for historical logic reasons, if we do not have a state we are approved
  return "approved";
};

const getPaymentTotalAmount = (da: API.AnyDualApproval): number => {
  if (isBulkTemplateDualApproval(da)) {
    return da.bulk_payment_template_version.payment_data.reduce(
      (sum, p) => sum + p.amount,
      0,
    );
  }

  if (isNachaPassthroughDualApproval(da)) {
    return (
      da.nacha_passthrough_submission.payment_total_debit_amount -
      da.nacha_passthrough_submission.payment_total_credit_amount
    );
  }

  if (isFedNowDualApproval(da)) {
    return da.fednow_transaction.amount_cents;
  }

  const relatedPayment =
    (da as API.DomesticWireDualApproval).wire ||
    (da as API.ACHDualApproval).ach_payment ||
    (da as API.FedNowDualApproval).fednow_transaction ||
    (da as API.ACHReversalDualApproval).unverified_ach_reversal ||
    (da as API.BulkReversalDualApproval).bulk_reversal ||
    (da as API.ScheduledACHDualApproval).scheduled_ach_payment ||
    (da as API.BulkDualApproval).bulk_payment ||
    (da as API.InternationalWireDualApproval).international_wire ||
    (da as API.WireImportBatchDualApproval).wire_import_batch;

  if (!relatedPayment) {
    return 0;
  }

  // For bulk dual approvals, use payment_total_amount, otherwise use amount
  if (isBulkDualApproval(da)) {
    return (da as API.BulkDualApproval).bulk_payment.payment_total_amount || 0;
  }

  if (isBulkReversalDualApproval(da)) {
    return Filters.dollarsToPennies(da.bulk_reversal.reversal_total_amount);
  }

  if (isWireImportBatchDualApproval(da)) {
    return da.wire_import_batch.total_amount;
  }

  if (isFedNowDualApproval(da)) {
    return da.fednow_transaction.amount_cents || 0;
  }

  return (relatedPayment as { amount?: number }).amount || 0;
};

const getTransactionType = (
  da: API.AnyDualApproval,
): "debit" | "credit" | undefined => {
  if (isACHDualApproval(da)) {
    return da.ach_payment.transaction_type;
  }
  if (isFedNowDualApproval(da)) {
    return "credit";
  }
  if (isScheduledACHDualApproval(da)) {
    return da.scheduled_ach_payment.transaction_type;
  }
  if (isACHReversalDualApproval(da)) {
    return (
      da.unverified_ach_reversal as { transaction_type?: "debit" | "credit" }
    ).transaction_type;
  }
  return undefined;
};

const getNetTotalDollarsWithSign = (
  da: API.AnyDualApproval,
): string | undefined => {
  if (isBulkDualApproval(da)) {
    const netTotal =
      da.bulk_payment.payment_total_debit_amount -
      da.bulk_payment.payment_total_credit_amount;
    const netTotalDollars = Filters.currency(netTotal as Cents, {
      hasDecimal: false,
    });
    return netTotal > 0 ? `+${netTotalDollars}` : netTotalDollars;
  }

  if (isBulkReversalDualApproval(da)) {
    const netTotal =
      Filters.dollarsToPennies(da.bulk_reversal.reversal_total_debit_amount) -
      Filters.dollarsToPennies(da.bulk_reversal.reversal_total_credit_amount);
    const netTotalDollars = Filters.currency(netTotal as Cents, {
      hasDecimal: false,
    });
    return netTotal > 0 ? `+${netTotalDollars}` : netTotalDollars;
  }

  if (
    isACHDualApproval(da) ||
    isFedNowDualApproval(da) ||
    isScheduledACHDualApproval(da) ||
    isACHReversalDualApproval(da)
  ) {
    const amount = getPaymentTotalAmount(da);
    const transactionType = getTransactionType(da);
    const formattedAmount = Filters.currency(amount as Cents, {
      hasDecimal: false,
    });

    if (transactionType === "debit") {
      return `+${formattedAmount}`;
    }
    if (transactionType === "credit") {
      return `-${formattedAmount}`;
    }
    return formattedAmount;
  }

  return undefined;
};

const getFromAccountId = (
  da: API.AnyDualApproval,
): API.AccountId | undefined => {
  if (isWireDualApproval(da)) return da.wire.from_account;
  if (isACHDualApproval(da)) return da.ach_payment.institution_account;
  if (isFedNowDualApproval(da)) return da.fednow_transaction.source_account;
  if (isACHReversalDualApproval(da))
    return da.unverified_ach_reversal.institution_account;
  if (isFedNowDualApproval(da)) return da.fednow_transaction.source_account;
  if (isScheduledACHDualApproval(da))
    return da.scheduled_ach_payment.institution_account;
  if (isBulkDualApproval(da)) return da.bulk_payment.institution_account;
  if (isBulkTemplateDualApproval(da))
    return da.bulk_payment_template_version.institution_account;
  if (isInternationalWireDualApproval(da))
    return da.international_wire.from_account;
  if (isWireImportBatchDualApproval(da))
    return da.wire_import_batch.from_account;
  return undefined;
};

const getDisplayType = (da: API.AnyDualApproval): string => {
  // TODO(PAYX-2453): the frontend label path is ready, but we are still
  // waiting on the backend to finish the bulk reversal dual approval payload.
  if (isBulkReversalDualApproval(da)) return "Bulk reversal";
  if (isACHReversalDualApproval(da)) return "ACH reversal";
  if (isFedNowDualApproval(da)) return "Instant";
  if (isScheduledACHDualApproval(da)) return "ACH";
  if (isFedNowDualApproval(da)) return "Instant";
  if (isACHDualApproval(da)) return "ACH";
  if (isBulkDualApproval(da)) return "Bulk";
  if (isBulkTemplateDualApproval(da)) return "Template";
  if (isInternationalWireDualApproval(da)) return "Intl wire";
  if (isWireDualApproval(da)) return "Domestic wire";
  if (isWireImportBatchDualApproval(da)) return "Wire import";
  if (isNachaPassthroughDualApproval(da)) return "ACH pass-thru";
  return "Unknown";
};

const getDualApprovalTypeFields = (
  da: API.AnyDualApproval,
  {
    templatePayments,
    templateCredits,
    templateDebits,
  }: {
    templatePayments: API.BulkTemplateVersion["payment_data"];
    templateCredits: API.BulkTemplateVersion["payment_data"];
    templateDebits: API.BulkTemplateVersion["payment_data"];
  },
) => {
  if (isWireDualApproval(da)) {
    return {
      wire: da.wire.id,
      recipientName: da.wire.beneficiary_name || "",
      wireMemo: [da.wire.memo_1, da.wire.memo_2, da.wire.memo_3, da.wire.memo_4]
        .filter(Boolean)
        .join(" "),
      wireReason: da.wire.wire_reason || "",
      templateName: da.wire.template_name || "",
      wireBankName: da.wire.to_account_institution_name || "",
      wireAccountNumber: da.wire.to_account_number || "",
      wireRoutingNumber: da.wire.to_account_routing_number || "",
      wireAddress: [
        da.wire.beneficiary_address_1,
        da.wire.beneficiary_address_2,
        da.wire.beneficiary_address_3,
      ]
        .filter(Boolean)
        .join(", "),
    };
  }

  if (isACHDualApproval(da)) {
    return {
      ach: da.ach_payment.id,
      recipientId: da.ach_payment.recipient,
      memo: da.ach_payment.description || "",
      note: da.ach_payment.memo || "",
      entryDescription: da.ach_payment.entry_desc || "",
      companyName: da.ach_payment.company_name || "",
      companyId: da.ach_payment.company_id || "",
      secCode: da.ach_payment.std_ent_cls_code || "",
      isSameDay: Boolean(
        (da.ach_payment as API.ACHPayment & PaymentWithSameDay).is_same_day,
      ),
    };
  }

  if (isACHReversalDualApproval(da)) {
    return {
      achReversal: da.unverified_ach_reversal.uuid,
      ach: da.unverified_ach_reversal.reversal_target_transaction,
      recipient: da.unverified_ach_reversal.recipient,
      memo: da.unverified_ach_reversal.memo || "",
      reversalReason: da.unverified_ach_reversal.reversal_reason,
    };
  }

  if (isFedNowDualApproval(da)) {
    return {
      fedNowTransaction: da.fednow_transaction.id,
      recipientId: da.fednow_transaction.unverified_recipient ?? undefined,
      memo: da.fednow_transaction.memo || "",
      paymentIdentification:
        da.fednow_transaction.payment_identification || undefined,
    };
  }

  if (isBulkReversalDualApproval(da)) {
    return {
      bulkReversal: da.bulk_reversal.id,
      bulkPayment: da.bulk_reversal.bulk_payment,
      memo: da.bulk_reversal.memo || "",
      reversalReason: da.bulk_reversal.reversal_reason,
      recipientCount: da.bulk_reversal.recipient_count || 0,
      paymentCount: da.bulk_reversal.reversal_count || 0,
      paymentCreditCount: da.bulk_reversal.reversal_credit_count || 0,
      paymentDebitCount: da.bulk_reversal.reversal_debit_count || 0,
      debitAmount: Filters.dollarsToPennies(
        da.bulk_reversal.reversal_total_debit_amount,
      ),
      creditAmount: Filters.dollarsToPennies(
        da.bulk_reversal.reversal_total_credit_amount,
      ),
    };
  }

  if (isBulkDualApproval(da)) {
    return {
      bulkPayment: da.bulk_payment.id,
      recipientCount: da.bulk_payment.recipient_count || 0,
      paymentCount: da.bulk_payment.payment_count || 0,
      paymentTitle: da.bulk_payment.title || "",
      isExpedited: da.bulk_payment.is_expedited || false,
      scheduledDates: da.bulk_payment.scheduled_dates,
      debitAmount: da.bulk_payment.payment_total_debit_amount,
      creditAmount: da.bulk_payment.payment_total_credit_amount,
      groupPosting: da.bulk_payment.group_posting || false,
      paymentCreditCount: da.bulk_payment.payment_credit_count || 0,
      paymentDebitCount: da.bulk_payment.payment_debit_count || 0,
      description: da.bulk_payment.description || "",
      entryDescription: da.bulk_payment.entry_desc || "",
      companyName: da.bulk_payment.company_name || "",
      companyId: da.bulk_payment.company_id || "",
      secCode: da.bulk_payment.sec_code || "",
      organizationFileType: da.bulk_payment.organization_file?.file_type,
    };
  }

  if (isBulkTemplateDualApproval(da)) {
    return {
      bulkPaymentTemplateVersion: da.bulk_payment_template_version.id,
      templateName: da.bulk_payment_template_version.name || "",
      versionNumber: da.bulk_payment_template_version.version_number,
      templateGroupHasActiveVersion: Boolean(
        da.bulk_payment_template_version.template_group_has_active_version,
      ),
      institutionAccountId:
        da.bulk_payment_template_version.institution_account,
      institutionAccountName:
        da.bulk_payment_template_version.institution_account_name ?? null,
      institutionAccountLastFour:
        da.bulk_payment_template_version.institution_account_last_four ?? null,
      secCode: da.bulk_payment_template_version.sec_code || "",
      entryDesc: da.bulk_payment_template_version.entry_desc || "",
      description: da.bulk_payment_template_version.description || "",
      maxExpediteChoice:
        da.bulk_payment_template_version.max_expedite_choice || 0,
      groupPosting: da.bulk_payment_template_version.group_posting || false,
      userChangeLog: da.bulk_payment_template_version.user_change_log || null,
      userFullName: da.bulk_payment_template_version.user_full_name || null,
      paymentData: da.bulk_payment_template_version.payment_data || [],
      paymentCount: templatePayments.length,
      paymentCreditCount: templateCredits.length,
      paymentDebitCount: templateDebits.length,
      creditAmount: templateCredits.reduce((sum, p) => sum + p.amount, 0),
      debitAmount: templateDebits.reduce((sum, p) => sum + p.amount, 0),
    };
  }

  if (isScheduledACHDualApproval(da)) {
    return {
      scheduledACH: da.scheduled_ach_payment.id,
      recipientId: da.scheduled_ach_payment.recipient,
      recurringRule: da.scheduled_ach_payment.recurring_rule,
      memo: da.scheduled_ach_payment.description || "",
      note: da.scheduled_ach_payment.memo || "",
      entryDescription: da.scheduled_ach_payment.entry_desc || "",
      companyName: da.scheduled_ach_payment.company_name || "",
      companyId: da.scheduled_ach_payment.company_id || "",
      secCode: da.scheduled_ach_payment.std_ent_cls_code || "",
      isSameDay: Boolean(
        (
          da.scheduled_ach_payment as API.ScheduledACHPayment &
            PaymentWithSameDay
        ).is_same_day,
      ),
    };
  }

  if (isInternationalWireDualApproval(da)) {
    return {
      international_wire: da.international_wire.id,
      recipientId: da.international_wire.recipient,
      swiftDestinationId: da.international_wire.destination,
      wireReason: da.international_wire.wire_reason || "",
      memo: da.international_wire.memo || "",
      note: da.international_wire.note || "",
      templateName: da.international_wire.template_name || "",
      quote: da.international_wire.quote || undefined,
    };
  }

  if (isWireImportBatchDualApproval(da)) {
    return {
      wireImportBatch: da.wire_import_batch.id,
      wireCount: da.wire_import_batch.wire_count,
      totalAmount: da.wire_import_batch.total_amount,
      fromAccount: da.wire_import_batch.from_account,
      description: da.wire_import_batch.description,
      recipientCount: da.wire_import_batch.recipient_count,
      fileName: da.wire_import_batch.file_name,
      note: da.wire_import_batch.note,
    };
  }

  if (isNachaPassthroughDualApproval(da)) {
    return {
      nachaPassthroughSubmission: da.nacha_passthrough_submission.id,
      ...mapNachaPassthroughSubmissionFields(da.nacha_passthrough_submission),
      note: da.nacha_passthrough_submission.internal_note,
    };
  }

  return {};
};

const receiveDualApproval = (
  state: DualApprovalSlice,
  da: API.AnyDualApproval,
) => {
  const { uuid, created_at, requester, responder } = da;
  const status: StatusType = getStatus(da);
  const paymentTotalAmount = getPaymentTotalAmount(da);

  const templatePayments = isBulkTemplateDualApproval(da)
    ? da.bulk_payment_template_version.payment_data || []
    : [];
  const templateCredits = templatePayments.filter(
    (p) => p.transaction_type === "credit",
  );
  const templateDebits = templatePayments.filter(
    (p) => p.transaction_type === "debit",
  );

  state.byId[da.uuid] = {
    id: uuid,
    created_at,
    expired_at: da.expired_at,
    ...getDualApprovalTypeFields(da, {
      templatePayments,
      templateCredits,
      templateDebits,
    }),
    fromAccountId: getFromAccountId(da),
    ...(isWireDualApproval(da) && {
      fromAccountDisplay: da.wire.from_account_display,
    }),
    paymentTotalAmount,
    dollarAmount: Filters.penniesToDollars(paymentTotalAmount),
    displayType: getDisplayType(da),
    netTotalDollarsWithSign: getNetTotalDollarsWithSign(da),
    transactionType: getTransactionType(da),
    requester: {
      id: requester.id,
      name: User.deserialize(requester).getDescription(),
    },
    responder: responder
      ? {
          id: responder.id,
          name: User.deserialize(responder).getDescription(),
        }
      : null,
    status,
    rejectionReason: da.note,
  } as AnyDualApprovalState;
};

type ReceiveAction = ReturnType<typeof prepare>;

const dualApprovalSlice = createSlice({
  name: "dualApprovals",
  initialState,
  reducers: {
    receive: {
      prepare,
      reducer: (state, action: ReceiveAction) => {
        if ("dualApprovals" in action.payload) {
          const { dualApprovals } = action.payload;
          dualApprovals?.forEach((da) => {
            receiveDualApproval(state, da);
          });
        } else {
          const { dualApproval } = action.payload;
          receiveDualApproval(state, dualApproval);
        }
      },
    },
    upsertPage: (
      state,
      action: {
        payload: {
          pageNumber: number;
          results: API.AnyDualApproval[];
          total: number;
        };
      },
    ) => {
      const { results, total, pageNumber } = action.payload;

      results.forEach((da) => {
        receiveDualApproval(state, da);
      });

      state.pageMap = state.pageMap ?? {};
      state.pageMap[pageNumber] = results.map((da) => da.uuid);
      state.total = total;
    },
    upsertHistoryPage: (
      state,
      action: {
        payload: {
          pageNumber: number;
          results: API.AnyDualApproval[];
          total: number;
        };
      },
    ) => {
      const { results, total, pageNumber } = action.payload;

      results.forEach((da) => {
        receiveDualApproval(state, da);
      });

      state.historyPageMap = state.historyPageMap ?? {};
      state.historyPageMap[pageNumber] = results.map((da) => da.uuid);
      state.historyTotal = total;
    },
    upsertTemplatePage: (
      state,
      action: {
        payload: {
          pageNumber: number;
          results: API.AnyDualApproval[];
          total: number;
        };
      },
    ) => {
      const { results, total, pageNumber } = action.payload;

      results.forEach((da) => {
        receiveDualApproval(state, da);
      });

      state.templatePageMap = state.templatePageMap ?? {};
      state.templatePageMap[pageNumber] = results.map((da) => da.uuid);
      state.templateTotal = total;
    },
    clear: (state) => {
      state.byId = {};
      state.pageMap = {};
      state.total = 0;
      state.templatePageMap = {};
      state.templateTotal = 0;
      state.historyPageMap = {};
      state.historyTotal = 0;
    },
  },
});

export type ExtraAction = ReturnType<typeof dualApprovalSlice.actions.receive>;

export const { actions } = dualApprovalSlice;
export default {
  dualApprovals: dualApprovalSlice.reducer,
};
